import React, { useEffect, useState } from 'react'
import IProps, { IPopularStory } from './interface'
import NewsCard from '../NewsCard/NewsCard'
import classNames from 'classnames'
import BlockWrapper from '../../Layout/BlockWrapper/BlockWrapper'
import ContentTypeFilter from '../../Search/Element/ContentTypeFilter/ContentTypeFilter'
import FiltersMenu from '../../Search/Element/FiltersMenu/FiltersMenu'
import SearchInput from '../../Search/Element/SearchInput/SearchInput'
import Button from '../../Element/Button/Button'
import Loading from '../../Search/Element/Loading/Loading'
import ResultsCount from '../../Search/Element/ResultsCount/ResultsCount'
import Title from '../../Element/Title/Title'
import init from './_interactive'
import {
  defaults,
  END_POINTS,
  dataFetcher,
  filtersJoint,
  useBackButton,
  updateHistory,
  controlZIndexes,
} from '../../Search/Utils/utils'
import {
  ISearchResponse,
  ISearchBody,
  IResultItem,
  IGroupFacet,
  IFacet,
  ISortItem,
} from '../../Search/Utils/interface'
import INewsCards from '../NewsCard/interface'
import ITag from '../../Element/Tag/interface'
import IImage from '../../Element/Image/interface'
import { ISrcSet } from '../../Element/Picture/interface'
import {
  IFixedDensityResultImage,
  ISearchApiImage,
} from '../../Search/Element/ResultCard/interface'

const getFirstSrcFromDensitySrcset = (densitySrcset?: string) =>
  densitySrcset?.split(',')[0]?.trim().split(/\s+/)[0]

const hasFixedDensityImage = (
  image: unknown
): image is IFixedDensityResultImage => {
  if (!image || typeof image !== 'object') return false

  const candidate = image as IFixedDensityResultImage
  return Boolean(candidate.src || candidate.densitySrcset)
}

const isSearchApiImage = (image: unknown): image is ISearchApiImage => {
  if (!image || typeof image !== 'object' || hasFixedDensityImage(image)) {
    return false
  }

  const candidate = image as ISearchApiImage
  return Array.isArray(candidate.srcset)
}

const parseSrcset = (srcset: unknown): ISrcSet | undefined => {
  if (Array.isArray(srcset)) {
    return srcset as ISrcSet
  }

  if (typeof srcset === 'string') {
    try {
      const parsed = JSON.parse(srcset)
      if (Array.isArray(parsed)) {
        return parsed as ISrcSet
      }
    } catch {
      return [{ breakpoint: 1920, url: srcset }]
    }
  }

  return undefined
}

const toAbsoluteMediaUrl = (url: string, baseURL?: string): string => {
  const trimmed = url.trim()

  if (
    !trimmed ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('//')
  ) {
    return trimmed
  }

  if (trimmed.startsWith('/') && baseURL) {
    return `${baseURL.replace(/\/$/, '')}${trimmed}`
  }

  return trimmed
}

/** Use native dimensions from Cloudflare image CDN instead of fixed w/h (e.g. 152px). */
const normalizeCdnImageUrl = (url: string): string => {
  if (!url.includes('/cdn-cgi/image/')) return url

  return url.replace(/\bw=\d+/g, 'w=auto').replace(/\bh=\d+/g, 'h=auto')
}

const normalizeSrcsetUrl = (url: string, baseURL?: string): string =>
  url
    .split(',')
    .map((part) => {
      const trimmed = part.trim()
      const descriptorIndex = trimmed.search(/\s+/)

      if (descriptorIndex === -1) {
        return normalizeCdnImageUrl(toAbsoluteMediaUrl(trimmed, baseURL))
      }

      const imageUrl = trimmed.slice(0, descriptorIndex)
      const descriptor = trimmed.slice(descriptorIndex)

      return `${normalizeCdnImageUrl(toAbsoluteMediaUrl(imageUrl, baseURL))}${descriptor}`
    })
    .join(', ')

const DENSITY_BREAKPOINTS = [430, 744, 1920]

const densitySrcsetToPictureSrcset = (
  densitySrcset: string,
  baseURL?: string
): ISrcSet =>
  densitySrcset.split(/,\s+(?=\/)/).map((part, index) => {
    const trimmed = part.trim()
    const descriptorIndex = trimmed.search(/\s+\d+x$/)
    const imageUrl =
      descriptorIndex === -1 ? trimmed : trimmed.slice(0, descriptorIndex)

    return {
      breakpoint:
        DENSITY_BREAKPOINTS[index] ??
        DENSITY_BREAKPOINTS[DENSITY_BREAKPOINTS.length - 1],
      url: normalizeCdnImageUrl(toAbsoluteMediaUrl(imageUrl, baseURL)),
    }
  })

const normalizeSrcset = (srcset: ISrcSet, baseURL?: string): ISrcSet =>
  srcset.map((entry) => ({
    ...entry,
    url: normalizeSrcsetUrl(entry.url, baseURL),
  }))

/**
 * Normalise search API image payloads for NewsCard / Picture (srcset-based).
 */
const parseImage = (
  image: IResultItem['image'] | string | undefined,
  usePixelMask: boolean,
  baseURL?: string
): IImage | undefined => {
  if (!image) return undefined

  let parsed: unknown = image
  if (typeof image === 'string') {
    try {
      parsed = JSON.parse(image)
    } catch {
      return {
        alt: '',
        srcset: normalizeSrcset(
          [{ breakpoint: 1920, url: image }],
          baseURL
        ),
        usePixelMask,
      }
    }
  }

  if (!parsed || typeof parsed !== 'object') return undefined

  const record = parsed as Record<string, unknown>
  const alt = typeof record.alt === 'string' ? record.alt : ''

  if (hasFixedDensityImage(parsed)) {
    if (parsed.densitySrcset) {
      return {
        alt,
        srcset: densitySrcsetToPictureSrcset(parsed.densitySrcset, baseURL),
        usePixelMask,
      }
    }

    const src = parsed.src
      ? toAbsoluteMediaUrl(parsed.src, baseURL)
      : getFirstSrcFromDensitySrcset(parsed.densitySrcset)

    if (!src) return undefined

    return {
      alt,
      srcset: normalizeSrcset([{ breakpoint: 1920, url: src }], baseURL),
      usePixelMask,
    }
  }

  if (isSearchApiImage(parsed)) {
    if (parsed.srcset.length > 0) {
      return {
        alt: parsed.alt ?? alt,
        srcset: normalizeSrcset(parsed.srcset, baseURL),
        usePixelMask,
      }
    }

    if (parsed.url) {
      return {
        alt: parsed.alt ?? alt,
        srcset: normalizeSrcset(
          [{ breakpoint: 1920, url: parsed.url }],
          baseURL
        ),
        usePixelMask,
      }
    }
  }

  const srcset = parseSrcset(record.srcset)
  if (srcset) {
    return {
      ...(parsed as IImage),
      alt,
      srcset: normalizeSrcset(srcset, baseURL),
      usePixelMask,
    }
  }

  if (typeof record.url === 'string') {
    return {
      alt,
      srcset: normalizeSrcset(
        [{ breakpoint: 1920, url: record.url }],
        baseURL
      ),
      usePixelMask,
    }
  }

  if (typeof record.src === 'string') {
    return {
      alt,
      srcset: normalizeSrcset(
        [{ breakpoint: 1920, url: record.src }],
        baseURL
      ),
      usePixelMask,
    }
  }

  return undefined
}

/**
 * Transform API result item to NewsCard format
 */
const transformResultToNewsCard = (
  result: IResultItem,
  usePixelMask: boolean,
  hideContentType: boolean = false,
  clickablePills: boolean = false,
  baseURL?: string
): INewsCards => {
  const parseTags = (): Array<ITag> | undefined => {
    if (result.tags) {
      if (typeof result.tags === 'string') {
        return result.tags
          .split(',')
          .map((tag: string) => tag.trim())
          .filter((tag: string) => tag.length > 0)
          .map((tag: string) => ({ text: tag }))
      } else if (Array.isArray(result.tags)) {
        return result.tags.map((tag): ITag => {
          if (typeof tag === 'string') {
            return { text: tag }
          } else {
            const tagText = tag.text || tag.name || ''
            const tagHref = tag.taglink.href || tag.href

            const rawHref = tagHref?.trim() || null

            return {
              text: tagText,
              href: clickablePills && rawHref ? rawHref : undefined,
              target: clickablePills && rawHref ? '_self' : undefined,
              classes: tag?.classes || undefined,
            }
          }
        })
      }
    }

    if (result.studyLevels && result.studyLevels.length > 0) {
      return result.studyLevels.map((level) => ({ text: level.name }))
    }

    return undefined
  }

  return {
    title: {
      text: result.title,
      type: 'h3',
    },
    subtitle: hideContentType ? undefined : result.contentType,
    description: result.blurb || '',
    image: parseImage(result.image, usePixelMask, baseURL),
    cta: {
      href: result.url,
      target: '_self',
    },
    tags: parseTags(),
  }
}

const FILTERS_PER_ROW = 5

const FeatureGridFilter = (props: IProps) => {
  const {
    pageId = '',
    baseURL = '',
    defaultFilters = [],
    defaultSort,
    cards: staticCards = [],
    usePixelMask = false,
    groupFacets: initialGroupFacets = [],
    selectedFilter: initialSelectedFilter = 'all',
    popularStories = [],
    showPixels,
    showTags = true,
    blockTheme,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    contentTypes = [],
    title,
    titleHeadingLevel = 'none',
    titleFont = 'serif',
    titleColor = 'secondary',
    hideSearch = false,
    hideFilters = false,
    filterTitle,
    popularStoriesTitle,
    hideContentType = false,
    searchPlaceHolderText,
    clickablePills = false,
    ...rest
  } = props

  // Determine if we're using API mode or static mode
  const isApiMode = Boolean(pageId && baseURL)

  // API-related state (similar to SearchResults)
  const [loading, setLoading] = useState(isApiMode)
  const [error, setError] = useState(false)
  const [loadMore, setLoadMore] = useState(false)
  const [apiResults, setApiResults] = useState<IResultItem[] | undefined>()
  const [currentSearch, setCurrentSearch] = useState<ISearchBody | undefined>()
  const [previousSearch, setPreviousSearch] = useState<
    ISearchBody | undefined
  >()
  const [pagination, setPagination] = useState<
    ISearchResponse['pagination'] | undefined
  >()
  const [groupFacets, setGroupFacets] =
    useState<IGroupFacet[]>(initialGroupFacets)
  const [facets, setFacets] = useState<IFacet[]>([])
  const [originalFacets, setOriginalFacets] = useState<IFacet[]>([])
  const [sortOptions, setSortOptions] = useState<ISortItem[] | undefined>()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedFacets, setSelectedFacets] = useState<
    IFacet['value'][] | undefined
  >()
  const [totalItems, setTotalItems] = useState<number>(0) // Store total items count for accurate filter counts

  // UI state
  const [selectedFilter, setSelectedFilter] = useState(initialSelectedFilter)
  const [visibleFilterRows, setVisibleFilterRows] = useState(1)
  const [openFacets, setOpenFacets] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [displayedCardsCount, setDisplayedCardsCount] = useState(5) // Only used in static mode

  // Track if interactive features have been initialized
  const [interactiveInitialized, setInteractiveInitialized] = useState(false)

  // Default search body for API requests
  const defaultSearch: ISearchBody = {
    Group: defaults.group,
    Page: 1,
    pageId,
    pageSize: defaults.pageSize,
    sortType: defaultSort ? 'DefaultSort' : '',
    sortValue: defaultSort || '',
    Filters: [...defaultFilters],
  }

  // Prepare facets similar to CourseTable (filter out any unwanted facets if needed)
  const prepareFacets = (bulkFacets: IFacet[]) => {
    // For FeatureGridFilter, return all facets - can be customized later if needed
    return bulkFacets
  }

  const prepareSortOptions = (bulkSortOptions: ISortItem[]) => {
    return bulkSortOptions.map((sortOption) => ({
      ...sortOption,
      value: sortOption.sortValue,
    }))
  }

  // Helper function to format content types to their plural form
  const formatContentTypeLabel = (contentType: string): string => {
    if (!contentType) return 'stories'

    // Common pluralization rules
    const contentTypeLower = contentType.toLowerCase()
    const secondLastChar =
      contentType.length > 1 ? contentTypeLower[contentType.length - 2] : ''

    // Handle words ending in consonant + 'y' -> remove 'y' and add 'ies'
    // "Story" -> "stories", "Category" -> "categories"
    if (
      contentTypeLower.endsWith('y') &&
      !['a', 'e', 'i', 'o', 'u'].includes(secondLastChar)
    ) {
      return contentType.slice(0, -1) + 'ies'
    }

    if (
      contentTypeLower.endsWith('s') ||
      contentTypeLower.endsWith('x') ||
      contentTypeLower.endsWith('z')
    ) {
      return contentType
    }

    // Handle words ending in 'ch' or 'sh' -> add 'es'
    // "Research" -> "researches"
    if (contentTypeLower.endsWith('ch') || contentTypeLower.endsWith('sh')) {
      return contentType + 'es'
    }

    return contentType + 's'
  }

  // Transform API facets to filter pills format
  const transformedFacets = React.useMemo(() => {
    const currentFacetsHaveCounts = facets.some(
      (facet) => facet.filters && facet.filters.some((filter) => (filter.count || 0) > 0)
    )
    
    const shouldUseOriginalFacets = 
      originalFacets.length > 0 && 
      (searchTerm || !currentFacetsHaveCounts)
    
    const facetsToUse = shouldUseOriginalFacets 
      ? originalFacets 
      : facets.length > 0 
      ? facets 
      : originalFacets

    if (!facetsToUse || facetsToUse.length === 0) return []

    // Get ALL facets that have filters
    const activeFacets = facetsToUse.filter(
      (facet) => facet.filters && facet.filters.length > 0
    )

    if (!activeFacets || activeFacets.length === 0) {
      return []
    }

    // Combine all filters from all facets into a single array
    const allFilters: Array<{ value: string; name: string; count: number }> = []

    activeFacets.forEach((facet) => {
      if (facet.filters) {
        facet.filters.forEach((filter) => {
          // Remove duplicates (same filter value)
          if (!allFilters.some((f) => f.value === filter.value)) {
            allFilters.push({
              value: filter.value,
              name: filter.name,
              count: filter.count,
            })
          } else {
            // If duplicate exists, update count to be the maximum
            const existingFilter = allFilters.find(
              (f) => f.value === filter.value
            )
            if (existingFilter) {
              existingFilter.count = Math.max(
                existingFilter.count,
                filter.count
              )
            }
          }
        })
      }
    })

    // Get the first content type from props or default to 'Story'
    // Use filterTitle if provided, otherwise use the formatted content type label
    const firstContentType =
      contentTypes && contentTypes.length > 0 ? contentTypes[0] : 'Story'
    const allContentTypeLabel = filterTitle
      ? filterTitle
      : 'All ' + formatContentTypeLabel(firstContentType)

    // Use totalItems from pagination for accurate count
    // This ensures the count matches the actual number of items in the group
    const allStoriesCount =
      totalItems > 0
        ? totalItems
        : allFilters.reduce((total, filter) => total + filter.count, 0)

    // Add "All" option at the beginning with dynamic content type label
    const allOption = {
      value: 'all',
      name: allContentTypeLabel,
      count: allStoriesCount,
    }

    // Cap individual filter counts at totalItems to prevent showing counts higher than available items
    const filterOptions = allFilters.map((filter) => ({
      value: filter.value,
      name: filter.name,
      count: totalItems > 0 ? Math.min(filter.count, totalItems) : filter.count,
    }))

    const result = [allOption, ...filterOptions]

    return result
  }, [originalFacets, facets, totalItems, contentTypes, filterTitle, searchTerm])

  const searchPlaceholderText = React.useMemo(() => {
    // Use provided searchPlaceHolderText if available, otherwise generate dynamic placeholder
    if (searchPlaceHolderText) {
      return searchPlaceHolderText
    }
    const firstContentType =
      contentTypes && contentTypes.length > 0 ? contentTypes[0] : 'Story'
    const formattedContentType = formatContentTypeLabel(firstContentType)
    return `Search ${formattedContentType.toLowerCase()}...`
  }, [contentTypes, searchPlaceHolderText])

  /**
   * This gets the page URL params and puts them into pageParams, ready to be sent to back-end for a search.
   */
  const populateFromPageParams = () => {
    if (!isApiMode) return

    const rawParameters = window.location.search
    const params = Object.fromEntries(new URLSearchParams(rawParameters))
    const paramKeys = Object.keys(params)
    paramKeys.forEach((param) => {
      if (!defaults.authorizedKeys.includes(param)) {
        delete params[param]
      }
    })

    const effectiveSortValue =
      defaultSearch.sortValue &&
      (!params.sortvalue || params.sortvalue === 'Relevance')
        ? defaultSearch.sortValue
        : params.sortvalue || defaultSearch.sortValue

    const effectiveSortType =
      defaultSearch.sortType &&
      (!params.sorttype ||
        (params.sortvalue === 'Relevance' && defaultSearch.sortValue))
        ? defaultSearch.sortType
        : params.sorttype || defaultSearch.sortType

    const body: ISearchBody = {
      ...defaultSearch,
      Group: params.group || defaultSearch.Group,
      Page: parseInt(params.page, 10) || defaultSearch.Page,
      pageId: params.pageid || defaultSearch.pageId,
      pageSize: parseInt(params.pagesize, 10) || defaultSearch.pageSize,
      sortType: effectiveSortType,
      sortValue: effectiveSortValue,
      Term: params.term || '',
      ...(params.filters && params?.filters !== ''
        ? {
            Filters: params.filters.split(filtersJoint),
          }
        : { Filters: [] }),
    }

    // Sync selectedFacets with URL params (like CourseTable)
    if (body.Filters && body.Filters.length > 0) {
      setSelectedFacets(body.Filters)
      // Update selectedFilter for pills display
      setSelectedFilter(body.Filters[0])
    } else {
      setSelectedFacets(undefined)
      setSelectedFilter('all')
    }

    setCurrentSearch(body)
  }

  // If back/forward buttons are used, proceed to a new search with the URL parameters
  useBackButton(populateFromPageParams)

  /**
   * Fetch data from API (similar to SearchResults)
   */
  const proceedToSearch = async (searchBody: ISearchBody) => {
    if (!isApiMode) return

    if (
      !previousSearch ||
      JSON.stringify(previousSearch) !== JSON.stringify(searchBody)
    ) {
      setPreviousSearch(searchBody)
      if (!loadMore) {
        setLoading(true)
        setApiResults(undefined)
      }

      try {
        // Create API payload - include pageId like SearchResults and CourseTable
        const apiPayload = { ...searchBody }

        // Ensure pageId is always included (from searchBody or fallback to prop)
        if (!apiPayload.pageId) {
          apiPayload.pageId = pageId
        }

        // Use Group from searchBody, fallback to defaults.group if not set (like CourseTable)
        if (!apiPayload.Group) {
          apiPayload.Group = defaults.group
        }

        apiPayload.blockname = 'featuregridfilter'

        // Add timeout to prevent hanging API calls
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('API request timeout')), 10000) // 10 second timeout
        })

        const apiPromise = dataFetcher(
          `${baseURL}${END_POINTS.search}`,
          apiPayload
        )

        const response: ISearchResponse = await Promise.race([
          apiPromise,
          timeoutPromise,
        ])

        if (response?.results) {
          setError(false)
          updateHistory(response.pagination.resultsPath)
          setPagination(response.pagination)
          setTotalItems(response.pagination?.totalItems || 0) // Store total items for accurate filter counts

          if (loadMore) {
            // Append new results to existing results (like CourseTable)
            setApiResults((results) => {
              setLoadMore(false)
              return [...(results || []), ...response.results]
            })
          } else {
            // Replace results with new results
            setGroupFacets(response.categorisation.groupFacets || [])
            setFacets(prepareFacets(response.categorisation.facets || [])) // Use prepareFacets like CourseTable
            setSortOptions(response.categorisation.sortItems) // Store sortItems like CourseTable

            const isInitialLoad = 
              (!searchBody.Filters || searchBody.Filters.length === 0) &&
              (!searchBody.Term || searchBody.Term === '') &&
              (searchBody.Group === defaults.group || !searchBody.Group)
            
            setOriginalFacets((prevOriginalFacets) => {
              if (isInitialLoad && response.categorisation.facets && response.categorisation.facets.length > 0) {
                return response.categorisation.facets
              } else if (prevOriginalFacets.length === 0 && response.categorisation.facets && response.categorisation.facets.length > 0) {
                return response.categorisation.facets
              } else {
                return prevOriginalFacets
              }
            })

            // Sync selectedFacets with currentSearch.Filters (like CourseTable)
            if (searchBody.Filters && searchBody.Filters.length > 0) {
              setSelectedFacets(searchBody.Filters)
              // Update selectedFilter for pills display (use first filter)
              setSelectedFilter(searchBody.Filters[0])
            } else {
              setSelectedFacets(undefined)
              setSelectedFilter('all')
            }

            setApiResults(response.results)
          }
          setLoading(false)
        } else {
          console.error('FeatureGridFilter: Invalid API response', response)
          setError(true)
          setLoading(false)
        }
      } catch (err) {
        console.error('FeatureGridFilter API Error:', err)
        setError(true)
        setLoading(false)
      }
    }
  }

  const effectiveUsePixelMask =
    showPixels !== undefined ? showPixels : usePixelMask

  // Get cards based on mode (API or static)
  const cards: INewsCards[] = React.useMemo(() => {
    const cardsSource: INewsCards[] =
      isApiMode && apiResults
        ? apiResults.map((result) =>
            transformResultToNewsCard(
              result,
              effectiveUsePixelMask,
              hideContentType,
              clickablePills,
              baseURL
            )
          )
        : staticCards

    return cardsSource.map((card) => {
      const updatedCard = { ...card }

      if (updatedCard.cta) {
        if (!updatedCard.cta.text || updatedCard.cta.text.trim().length === 0) {
          updatedCard.cta = {
            ...updatedCard.cta,
            text: 'Read more',
          }
        }
      }

      if (!showTags) {
        updatedCard.tags = []
      }

      // Conditionally remove subtitle based on hideContentType prop
      if (hideContentType) {
        updatedCard.subtitle = undefined
      }

      if (updatedCard.image) {
        updatedCard.image = {
          ...updatedCard.image,
          usePixelMask: effectiveUsePixelMask,
        }
      }

      return updatedCard
    })
  }, [
    isApiMode,
    apiResults,
    staticCards,
    effectiveUsePixelMask,
    showTags,
    hideContentType,
  ])

  // In static mode, filter cards client-side
  const filteredCards = React.useMemo(() => {
    // In API mode, filtering is done server-side
    if (isApiMode) {
      return cards
    }

    // Static mode: client-side filtering
    let filtered = cards

    // Apply topic filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter((card) =>
        card.tags?.some((tag) => {
          // Map filter values to tag text for matching
          const filterValueMap: { [key: string]: string } = {
            sustainability: 'Sustainability',
            innovation: 'Innovation',
            research: 'Research',
            education: 'Education',
            technology: 'Technology',
            health: 'Health',
            environment: 'Environment',
            social: 'Social Impact',
            business: 'Business',
          }
          return tag.text === filterValueMap[selectedFilter]
        })
      )
    }

    // Apply search filter
    if (searchTerm && searchTerm.trim() !== '') {
      const lowerSearchTerm = searchTerm.toLowerCase().trim()
      filtered = filtered.filter((card) => {
        const titleText = card.title?.text?.toLowerCase() || ''
        const descriptionText = card.description?.toLowerCase() || ''
        const subtitleText = card.subtitle?.toLowerCase() || ''
        const categoryText = card.categoryName?.toLowerCase() || ''

        return (
          titleText.includes(lowerSearchTerm) ||
          descriptionText.includes(lowerSearchTerm) ||
          subtitleText.includes(lowerSearchTerm) ||
          categoryText.includes(lowerSearchTerm)
        )
      })
    }

    return filtered
  }, [isApiMode, cards, selectedFilter, searchTerm])

  // Handle filter selection from ContentTypeFilter (pills)
  // This follows CourseTable's logic but works with pills display
  const handleFilterSelect = (filterValue: string) => {
    // Deselection logic to check whether the currently selected filter is being deselected
    const currentFilters =
      isApiMode && currentSearch ? currentSearch.Filters || [] : []
    const isCurrentlySelected =
      filterValue === selectedFilter ||
      (currentFilters.length > 0 && currentFilters[0] === filterValue)

    const isDeselecting =
      filterValue === 'All' ||
      filterValue === defaults.group ||
      (isCurrentlySelected && filterValue !== 'all' && selectedFilter !== 'all')

    if (isDeselecting) {
      // Deselect the filter - set to 'all' and remove from Filters
      setSelectedFilter('all')
      setVisibleFilterRows(1)

      // In API mode, trigger new search with empty filters
      if (isApiMode && currentSearch) {
        const body: ISearchBody = {
          ...currentSearch,
          Group: currentSearch.Group || defaults.group,
          Page: defaultSearch.Page, // Reset to first page
          Filters: [], // Remove filter from body
        }
        setLoadMore(false) // Reset loadMore state when filter changes
        setSelectedFacets(undefined)
        setCurrentSearch(body)
      }
      return
    }

    // Normal selection
    setSelectedFilter(filterValue)

    // Reset to default state when "All" option is clicked
    if (filterValue === 'all') {
      setVisibleFilterRows(1)
    }

    // In API mode, trigger new search with updated filter
    // Use array-based filters like CourseTable
    if (isApiMode && currentSearch) {
      const newFilters = filterValue === 'all' ? [] : [filterValue]
      const body: ISearchBody = {
        ...currentSearch,
        Group: currentSearch.Group || defaults.group, // Use defaults.group like CourseTable
        Page: defaultSearch.Page, // Reset to first page
        Filters: newFilters,
      }
      setLoadMore(false) // Reset loadMore state when filter changes
      setSelectedFacets(newFilters.length > 0 ? newFilters : undefined)
      setCurrentSearch(body)
    }
  }

  // Handle "More +" button click - show one additional row of filters
  const handleShowMore = () => {
    setVisibleFilterRows((prevRows) => Math.min(prevRows + 1, totalFilterRows))
  }

  // Helper function to close mobile menu after delay
  const closeMobileMenu = () => {
    setTimeout(() => {
      const menuButton = document.querySelector(
        '.featureGridFilter__filter-container--mobile .search-filters button'
      ) as HTMLButtonElement
      if (menuButton) {
        menuButton.click()
      }
    }, 300)
  }

  const facetsAndSortCallback = (
    selectedFilters: string[],
    sortType?: string,
    sortValue?: string
  ) => {
 
    const isFilterSelectionFromRadio = sortType === 'topic'
    const hasSelectedFilters = selectedFilters && selectedFilters.length > 0
    
 
    const derivedFilters = isFilterSelectionFromRadio && sortValue
      ? (sortValue === 'all' ? [] : [sortValue])
      : hasSelectedFilters
      ? selectedFilters
      : (sortValue && sortValue !== 'all' ? [sortValue] : [])

    // Prevent processing disabled filters when using sortValue
    if (!hasSelectedFilters && sortValue) {
      const selectedFilter = filtersToUse.find(
        (facet) => facet.value === sortValue
      )

      if (
        selectedFilter &&
        sortValue !== 'all' &&
        (selectedFilter.count || 0) === 0
      ) {
        return
      }
    }

    const updateSelectedState = () => {
      setSelectedFacets(derivedFilters.length > 0 ? derivedFilters : undefined)
      if (derivedFilters.length === 0) {
        setSelectedFilter('all')
      } else {
        setSelectedFilter(derivedFilters[0])
      }
    }

    // Static mode relies purely on local state
    if (!isApiMode || !currentSearch) {
      updateSelectedState()
      closeMobileMenu()
      return
    }

    const isFilterSelection = hasSelectedFilters || isFilterSelectionFromRadio
    const nextSortType = isFilterSelection
      ? currentSearch.sortType 
      : (sortType || currentSearch.sortType)
    const nextSortValue = isFilterSelection
      ? currentSearch.sortValue 
      : (sortValue || currentSearch.sortValue)
    
    const filtersMatch =
      JSON.stringify(currentSearch.Filters || []) ===
      JSON.stringify(derivedFilters)
    const sortTypeMatch = nextSortType === currentSearch.sortType
    const sortValueMatch = nextSortValue === currentSearch.sortValue

    const localStateInSync =
      JSON.stringify(selectedFacets || []) === JSON.stringify(derivedFilters) &&
      selectedFilter === (derivedFilters.length > 0 ? derivedFilters[0] : 'all')

    if (filtersMatch && sortTypeMatch && sortValueMatch && localStateInSync) {
 
      closeMobileMenu()
      return
    }

    updateSelectedState()
    if (!filtersMatch || !sortTypeMatch || !sortValueMatch) {
      const body: ISearchBody = {
        ...currentSearch,
        Group: currentSearch.Group || defaults.group,
        Page: defaultSearch.Page,
        Filters: derivedFilters,
        sortType: nextSortType,
        sortValue: nextSortValue,
      }

      setLoadMore(false)
      setCurrentSearch(body)
    }

    closeMobileMenu()
  }

  // Handle open facets callback
  const handleOpenFacetsCallback = (facets: string[] | undefined) => {
    setOpenFacets(facets || [])
  }

  // Handle search input callback
  const handleSearchCallback = (term: string) => {
    setSearchTerm(term)

    // In API mode, trigger new search with search term
    if (isApiMode && currentSearch) {
      const body: ISearchBody = {
        ...currentSearch,
        Term: term || '', // Ensure Term is always a string
        // Use Group from currentSearch, or fallback to defaults.group
        Group: currentSearch.Group || defaults.group,
        Page: defaultSearch.Page, // Reset to first page
        Filters: [], // Reset filters when searching
      }
      setLoadMore(false) // Reset loadMore state when searching
      setSelectedFacets(undefined) // Clear selected facets when searching
      setSelectedFilter('all') // Reset pills to 'all'
      setCurrentSearch(body)
    }
  }

  // Handle load more button click - fetches next page from API (like CourseTable)
  const handleLoadMore = () => {
    if (isApiMode && currentSearch && pagination) {
      const nextPage = (pagination.currentPage || 1) + 1
      const body: ISearchBody = {
        ...currentSearch,
        Page: nextPage,
      }
      setLoadMore(true)
      setCurrentSearch(body)
    } else {
      // Fallback to client-side pagination for static mode
      setDisplayedCardsCount((prevCount) => {
        const newCount = prevCount + 5
        return newCount
      })
    }
  }

  // Reset displayed cards count when filter or search changes (only for static mode)
  useEffect(() => {
    if (!isApiMode) {
      setDisplayedCardsCount(5)
    }
  }, [selectedFilter, searchTerm, isApiMode])

  /**
   * Each time the current search body is updated, proceed to search
   * Similar to SearchResults component
   */
  useEffect(() => {
    if (!isApiMode) return

    if (!currentSearch) {
      // Initial load in API mode - populate from URL params or use defaultSearch
      // If URL has params, use them; otherwise use defaultSearch with defaultFilters
      const hasUrlParams = window.location.search.length > 0
      if (hasUrlParams) {
        populateFromPageParams()
      } else {
        // Use defaultSearch with defaultFilters and sync state
        setCurrentSearch(defaultSearch)
        if (defaultFilters.length > 0) {
          setSelectedFacets(defaultFilters)
          setSelectedFilter(defaultFilters[0])
        } else {
          setSelectedFacets(undefined)
          setSelectedFilter('all')
        }
      }
    } else {
      // Proceed with search
      proceedToSearch(currentSearch)
    }
  }, [currentSearch, isApiMode])

  // Determine which filters to show - use API facets in API mode, groupFacets in static mode
  const filtersToUse = isApiMode ? transformedFacets : groupFacets

  const fallbackFilters = React.useMemo(() => {
    if (!isApiMode || !originalFacets || originalFacets.length === 0) return []
    
    const allFilters: Array<{ value: string; name: string; count: number }> = []
    originalFacets.forEach((facet) => {
      if (facet.filters) {
        facet.filters.forEach((filter) => {
          if (!allFilters.some((f) => f.value === filter.value)) {
            allFilters.push({
              value: filter.value,
              name: filter.name,
              count: filter.count || 0,
            })
          }
        })
      }
    })
    
    const firstContentType = contentTypes && contentTypes.length > 0 ? contentTypes[0] : 'Story'
    const allContentTypeLabel = filterTitle
      ? filterTitle
      : 'All ' + formatContentTypeLabel(firstContentType)
    
    const allOption = {
      value: 'all',
      name: allContentTypeLabel,
      count: 0,
    }
    
    return [allOption, ...allFilters]
  }, [originalFacets, isApiMode, contentTypes, filterTitle])

  // Filter out disabled filters (count === 0, except for "all" option)
  // If all filters are filtered out and we have fallback filters, use those instead
  // This ensures filters are shown even when search returns no results
  const enabledFilters = React.useMemo(() => {
    if (!filtersToUse || filtersToUse.length === 0) {
      return fallbackFilters.length > 0 ? fallbackFilters : []
    }
    
    const filtered = filtersToUse.filter(
      (facet) => facet.value === 'all' || (facet.count || 0) > 0
    )
    
    const hasOnlyAllOption = filtered.length === 1 && filtered[0]?.value === 'all'
    const hasNoFilters = filtered.length === 0
    
    if ((hasOnlyAllOption || hasNoFilters) && fallbackFilters.length > 1) {
      return fallbackFilters
    }
    
    return filtered
  }, [filtersToUse, fallbackFilters])

  const totalFilterRows = Math.max(
    1,
    Math.ceil((enabledFilters?.length || 0) / FILTERS_PER_ROW)
  )
  const maxVisibleFilters = Math.min(
    visibleFilterRows * FILTERS_PER_ROW,
    enabledFilters.length
  )
  const visibleFilters = enabledFilters.slice(0, maxVisibleFilters)
  const hasMoreFilters = enabledFilters.length > maxVisibleFilters

  // Button is disabled only when there are no more filters to show, regardless of their count
  const isMoreButtonDisabled = !hasMoreFilters

  useEffect(() => {
    setVisibleFilterRows((prevRows) => Math.min(prevRows, totalFilterRows))
  }, [totalFilterRows])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const preparedSortOptions = React.useMemo(() => {
    if (!sortOptions || sortOptions.length === 0) return undefined
    return prepareSortOptions(sortOptions)
  }, [sortOptions])

  // Transform filtersToUse to sortItems structure for FiltersMenu (radio buttons)
  // Use the same data source as desktop filter pills
  // Filter out disabled items (count === 0, except for "all" option)

  const transformedSortItems = React.useMemo(() => {
    if (!filtersToUse || filtersToUse.length === 0) return []

    return filtersToUse
      .filter((facet) => facet.value === 'all' || (facet.count || 0) > 0)
      .map((facet) => ({
        sortType: 'topic',
        name: facet.name,
        value: facet.value,
        selected: selectedFilter === facet.value,
        count: facet.count || 0,
        // Disable filters with count 0 (except for "all" option)
        disabled: facet.value !== 'all' && (facet.count || 0) === 0,
      }))
  }, [filtersToUse, selectedFilter])

  // Get cards to display
  // In API mode with pagination, show all loaded results; otherwise use displayedCardsCount
  const cardsToDisplay =
    isApiMode && pagination
      ? filteredCards // Show all loaded results in API mode
      : filteredCards.slice(0, displayedCardsCount) // Client-side pagination for static mode

  // Check if there are more cards to load
  const hasMoreCards =
    isApiMode && pagination
      ? (pagination.currentPage || 1) < (pagination.totalPages || 1) // Check pagination in API mode
      : filteredCards.length > displayedCardsCount // Check array length in static mode

  const listingCount = Math.min(cardsToDisplay.length, 5)

  // Determine the position for Popular Stories (always at bottom of right column)
  // Popular Stories is NOT counted as a card - it's positioned AFTER all news cards
  // Right column positions are: 1, 3, 5, 6, 8, 10, 11, 13, 15, 16, 18, 20...
  // This ensures Popular Stories is always at the bottom right, and all cards are displayed
  const getPopularStoriesPosition = () => {
    if (!popularStories || popularStories.length === 0) return null

    // Use cardsToDisplay.length which represents actual displayed cards (Popular Stories NOT included)
    // In API mode: all loaded results, in static mode: sliced by displayedCardsCount
    const displayedCount = cardsToDisplay.length

    // If we have no cards, return null
    if (displayedCount === 0) return null

    // If we have fewer than 6 cards, Popular Stories should be in the first set (position 5)
    // This ensures Popular Stories is NOT shown in additional sets when results < 6
    if (displayedCount < 6) {
      return 5 // Position 5 is the bottom right of the first set
    }

    // For 6+ cards, Popular Stories should be in the LAST additional set that was created
    // Each time "Load more stories" is clicked, it creates a new additional set
    // Popular Stories moves to that new set

    // Calculate which additional set we're in (0 = first set, 1 = first additional, 2 = second additional, etc.)
    const totalSets = Math.ceil(displayedCount / 5)
    const lastAdditionalSetIndex = totalSets - 1 // Last set index (0-based, where 0 is first set)

    // If we're still in the first set, return position 5
    if (lastAdditionalSetIndex === 0) {
      return 5
    }

    // Calculate the start and end positions of the last additional set
    const lastSetStartPosition = lastAdditionalSetIndex * 5 + 1 // e.g., set 1 starts at 6, set 2 at 11, set 3 at 16
    const lastSetEndPosition = lastAdditionalSetIndex * 5 + 5 // e.g., set 1 ends at 10, set 2 at 15, set 3 at 20

    // Calculate how many cards are in the last additional set
    const cardsInLastSet = Math.min(
      displayedCount - lastAdditionalSetIndex * 5,
      5
    )
    const lastCardInLastSet = lastAdditionalSetIndex * 5 + cardsInLastSet

    // Right column positions within any set are: position 1, 3, 5 (of that set)
    // So for set 1 (positions 6-10): 6, 8, 10
    // For set 2 (positions 11-15): 11, 13, 15
    // For set 3 (positions 16-20): 16, 18, 20
    const rightColumnPositionsInSet = [
      lastSetStartPosition, // position 1 in set (e.g., 6, 11, 16)
      lastSetStartPosition + 2, // position 3 in set (e.g., 8, 13, 18)
      lastSetEndPosition, // position 5 in set (e.g., 10, 15, 20)
    ]

    // Find the first right column position that is after the last card in the last set
    for (const rightPos of rightColumnPositionsInSet) {
      if (rightPos > lastCardInLastSet) {
        return rightPos
      }
    }

    // If all right column positions are before the last card, use the last right column position (bottom right of last set)
    return lastSetEndPosition
  }

  const popularStoriesPosition = getPopularStoriesPosition()

  // Calculate which set and position within set for Popular Stories
  const getPopularStoriesSetInfo = () => {
    if (!popularStoriesPosition) return null

    const setIndex = Math.floor((popularStoriesPosition - 1) / 5)
    const positionInSet = ((popularStoriesPosition - 1) % 5) + 1

    return { setIndex, positionInSet }
  }

  const popularStoriesSetInfo = getPopularStoriesSetInfo()

  // Always use mask-10 for Popular Stories
  const pixelLayout = 10

  // Use popularStoriesTitle if provided, otherwise default to "POPULAR STORIES"
  const displayPopularStoriesTitle = popularStoriesTitle || 'POPULAR STORIES'

  const renderPopularStoryTags = (tags?: IPopularStory['tags']) => {
    if (!tags || tags.length === 0) return null

    return (
      <>
        {tags.map((tag, index) => (
          <span
            key={`popular-story-tag-${index}`}
            className='featureGridFilter__popular-story-tag'>
            {tag.text || tag.name || ''}
          </span>
        ))}
      </>
    )
  }

  // Initialize interactive features after content is loaded
  useEffect(() => {
    // Only initialize once
    if (interactiveInitialized) return

    // In API mode, only initialize after loading is complete and we have results
    if (isApiMode) {
      if (!loading && !error && filteredCards.length > 0) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          init()
          setInteractiveInitialized(true)
          // Emit custom event for Storybook to know content is ready
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('storybook:content-ready'))
          }
        }, 100)
      }
    } else {
      // In static mode, initialize immediately if we have cards
      if (filteredCards.length > 0) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(() => {
          init()
          setInteractiveInitialized(true)
          // Emit custom event for Storybook to know content is ready
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('storybook:content-ready'))
          }
        }, 100)
      }
    }
  }, [isApiMode, loading, error, filteredCards.length, interactiveInitialized])

  // Emit event for Storybook when error state changes
  useEffect(() => {
    if (error && isApiMode) {
      // Small delay to ensure error DOM is rendered
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('storybook:content-ready'))
        }
      }, 100)
    }
  }, [error, isApiMode])

  useEffect(() => {
    controlZIndexes()
  }, [])

  const blockWrapperProps = {
    ...rest,
    ...(blockTheme && { sectionTheme: blockTheme }),
    ...(paddingTop && { topPadding: paddingTop }),
    ...(paddingBottom && { bottomPadding: paddingBottom }),
    ...(paddingLeft && { leftIndent: paddingLeft }),
    ...(paddingRight && { rightIndent: paddingRight }),
  }

  return (
    <BlockWrapper {...blockWrapperProps}>
      {/* Loading State */}
      {loading && isApiMode && (
        <div className='featureGridFilter__loading'>
          <Loading text='Loading stories...' />
        </div>
      )}

      {/* Error State */}
      {error && isApiMode && (
        <div className='featureGridFilter__error'>
          <p>{defaults.error}</p>
        </div>
      )}

      {/* Content - only show if not loading or in static mode */}
      {(!loading || !isApiMode) && !error && (
        <>
          {/* Title - Show in original position, but hide on desktop when filters are hidden */}
          {title && (
            <Title
              id='featureGridFilter-title'
              type={titleHeadingLevel === 'none' ? 'p' : titleHeadingLevel}
              classes={classNames({
                ['featureGridFilter__title']: true,
                ['featureGridFilter__title--serif']: titleFont === 'serif',
                ['featureGridFilter__title--sans']: titleFont === 'sans serif',
                ['featureGridFilter__title--primary']: titleColor === 'primary',
                ['featureGridFilter__title--secondary']:
                  titleColor === 'secondary',
                ['featureGridFilter__title--hide-on-desktop']: hideFilters,
              })}>
              {title}
            </Title>
          )}

          {/* Filter Pills Container - Desktop */}
          {((!hideFilters && enabledFilters.length > 0) ||
            !hideSearch ||
            (hideFilters && title)) && (
            <div className='featureGridFilter__filter-container featureGridFilter__filter-container--desktop'>
              <div
                className={classNames('featureGridFilter__filters-row', {
                  'featureGridFilter__filters-row--no-search': hideSearch,
                  'featureGridFilter__filters-row--no-filters-no-title':
                    hideFilters && !title,
                })}>
                {!hideFilters && enabledFilters.length > 0 && (
                  <div
                    className={classNames('featureGridFilter__filters', {
                      'featureGridFilter__filters--expanded':
                        visibleFilterRows > 1,
                    })}>
                    <ContentTypeFilter
                      groupFacets={visibleFilters}
                      callBack={handleFilterSelect}
                      allowUnselect={true}
                      preSelected={selectedFilter}
                      showCounts={false}
                    />
                    {hasMoreFilters && (
                      <button
                        className={classNames(
                          'featureGridFilter__more-button',
                          {
                            'featureGridFilter__more-button--disabled':
                              isMoreButtonDisabled,
                          }
                        )}
                        onClick={handleShowMore}
                        disabled={isMoreButtonDisabled}
                        type='button'>
                        More +
                      </button>
                    )}
                  </div>
                )}
                {/* Title in filter row when filters are hidden on desktop */}
                {hideFilters && title && (
                  <div className='featureGridFilter__title-inline'>
                    <Title
                      id='featureGridFilter-title-inline'
                      type={
                        titleHeadingLevel === 'none' ? 'p' : titleHeadingLevel
                      }
                      classes={classNames({
                        ['featureGridFilter__title']: true,
                        ['featureGridFilter__title--serif']:
                          titleFont === 'serif',
                        ['featureGridFilter__title--sans']:
                          titleFont === 'sans serif',
                        ['featureGridFilter__title--primary']:
                          titleColor === 'primary',
                        ['featureGridFilter__title--secondary']:
                          titleColor === 'secondary',
                        ['featureGridFilter__title--inline']: true,
                      })}>
                      {title}
                    </Title>
                  </div>
                )}
                {!hideSearch && (
                  <div className='featureGridFilter__search-input'>
                    <SearchInput
                      baseURL={baseURL}
                      searchTerm={searchTerm}
                      currentContentType='DSH'
                      placeholderText={searchPlaceholderText}
                      showSubmitButtons={true}
                      callBack={handleSearchCallback}
                      useAutoCompleteAsSearch={true}
                      pageId={pageId}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Filters Menu - Mobile */}
          {((!hideFilters && enabledFilters.length > 0) || !hideSearch) && (
            <div
              className={classNames(
                'featureGridFilter__filter-container',
                'featureGridFilter__filter-container--mobile',
                {
                  'featureGridFilter__filter-container--no-filters':
                    hideFilters,
                }
              )}>
              <div className='featureGridFilter__filters-row'>
                {!hideFilters && enabledFilters.length > 0 && (
                  <FiltersMenu
                    facets={facets}
                    openFacets={openFacets}
                    openFacetsCallBack={handleOpenFacetsCallback}
                    sortItems={transformedSortItems}
                    buttonLabel='Filters'
                    callBack={facetsAndSortCallback}
                    closeOnRadioSelect={false}
                  />
                )}
                {!hideSearch && (
                  <div className='featureGridFilter__search-input'>
                    <SearchInput
                      baseURL={baseURL}
                      searchTerm={searchTerm}
                      currentContentType='DSH'
                      placeholderText={searchPlaceholderText}
                      showSubmitButtons={true}
                      callBack={handleSearchCallback}
                      useAutoCompleteAsSearch={true}
                      pageId={pageId}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Card Content */}
          <div className='featureGridFilter__content'>
            <div>
              {cardsToDisplay[1] && (
                <div
                  className={classNames(
                    'featureGridFilter__newsCard--2 featureGridFilter__newsCard',
                    { '!mb-0': listingCount === 2 }
                  )}>
                  <NewsCard
                    {...cardsToDisplay[1]}
                    image={{
                      ...cardsToDisplay[1].image!,
                      usePixelMask: false,
                      aspect: {
                        mobile: '1x1',
                        tablet: '1x1',
                        desktop: '1x1',
                      },
                    }}
                  />
                </div>
              )}
              {listingCount >= 4 && cardsToDisplay[3] && (
                <div
                  className={classNames(
                    'featureGridFilter__newsCard--4 featureGridFilter__newsCard',
                    { '!mb-0': listingCount === 4 || listingCount === 5 }
                  )}>
                  <NewsCard
                    {...cardsToDisplay[3]}
                    image={{
                      ...cardsToDisplay[3].image!,
                      usePixelMask: false,
                      aspect: {
                        mobile: '1x1',
                        tablet: '4x3',
                        desktop: '4x3',
                      },
                    }}
                  />
                </div>
              )}
              {cardsToDisplay.length >= 5 && cardsToDisplay[4] && (
                <div
                  className={classNames(
                    'featureGridFilter__newsCard--5 featureGridFilter__newsCard',
                    { '!mb-0': listingCount === 5 }
                  )}>
                  <NewsCard
                    {...cardsToDisplay[4]}
                    image={{
                      ...cardsToDisplay[4].image!,
                      usePixelMask: false,
                      aspect: {
                        mobile: '1x1',
                        tablet: '1x1',
                        desktop: '1x1',
                      },
                    }}
                  />
                </div>
              )}
            </div>
            <div className='featureGridFilter__rightLayout'>
              {/* Position 1 - Popular Stories or Card 1 */}
              {/* Popular Stories is positioned AFTER all cards, so it only shows if it's at position 1 in first set */}
              {popularStoriesSetInfo?.setIndex === 0 &&
              popularStoriesSetInfo?.positionInSet === 1 ? (
                <div className='featureGridFilter__newsCard--1 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop'>
                  <div
                    className={classNames(
                      'featureGridFilter__popular-stories',
                      `featureGridFilter__popular-stories--mask-${pixelLayout}`
                    )}>
                    <h2 className='featureGridFilter__popular-stories-title'>
                      {displayPopularStoriesTitle}
                    </h2>
                    <div className='featureGridFilter__popular-stories-list'>
                      {popularStories.map((story, index) => (
                        <div
                          key={`popular-story-desktop-${index}`}
                          className='featureGridFilter__popular-story'>
                          <a
                            href={story.link.href}
                            className='featureGridFilter__popular-story-link'
                            target={story.link.target || '_self'}>
                            <h3 className='featureGridFilter__popular-story-headline'>
                              {story.headline}
                            </h3>
                            {renderPopularStoryTags(story.tags)}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                cardsToDisplay[0] && (
                  <div className='featureGridFilter__newsCard--1 featureGridFilter__newsCard'>
                    <NewsCard
                      {...cardsToDisplay[0]}
                      image={{
                        ...cardsToDisplay[0].image!,
                        usePixelMask: effectiveUsePixelMask,
                        aspect: {
                          mobile: '1x1',
                          tablet: '4x3',
                          desktop: '4x3',
                        },
                      }}
                    />
                  </div>
                )
              )}
              {/* Position 3 - Popular Stories or Card 3 */}
              {/* Popular Stories is positioned AFTER all cards, so it only shows if it's at position 3 in first set */}
              {popularStoriesSetInfo?.setIndex === 0 &&
              popularStoriesSetInfo?.positionInSet === 3 ? (
                <div className='featureGridFilter__newsCard--3 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop'>
                  <div
                    className={classNames(
                      'featureGridFilter__popular-stories',
                      `featureGridFilter__popular-stories--mask-${pixelLayout}`
                    )}>
                    <h2 className='featureGridFilter__popular-stories-title'>
                      {displayPopularStoriesTitle}
                    </h2>
                    <div className='featureGridFilter__popular-stories-list'>
                      {popularStories.map((story, index) => (
                        <div
                          key={`popular-story-desktop-${index}`}
                          className='featureGridFilter__popular-story'>
                          <a
                            href={story.link.href}
                            className='featureGridFilter__popular-story-link'
                            target={story.link.target || '_self'}>
                            <h3 className='featureGridFilter__popular-story-headline'>
                              {story.headline}
                            </h3>
                            {renderPopularStoryTags(story.tags)}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                listingCount >= 3 &&
                cardsToDisplay[2] && (
                  <div
                    className={classNames(
                      'featureGridFilter__newsCard--3 featureGridFilter__newsCard',
                      { '!mb-0': listingCount === 3 }
                    )}>
                    <NewsCard
                      {...cardsToDisplay[2]}
                      image={{
                        ...cardsToDisplay[2].image!,
                        usePixelMask: effectiveUsePixelMask,
                        aspect: {
                          mobile: '1x1',
                          tablet: '1x1',
                          desktop: '1x1',
                        },
                      }}
                    />
                  </div>
                )
              )}
              {/* Position 5 - Popular Stories (at bottom of right column in first set if positioned there) */}
              {/* Popular Stories is positioned AFTER all cards, so it only shows if it's at position 5 in first set */}
              {popularStoriesSetInfo?.setIndex === 0 &&
              popularStoriesSetInfo?.positionInSet === 5 ? (
                <div className='featureGridFilter__newsCard--5 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop'>
                  <div
                    className={classNames(
                      'featureGridFilter__popular-stories',
                      `featureGridFilter__popular-stories--mask-${pixelLayout}`
                    )}>
                    <h2 className='featureGridFilter__popular-stories-title'>
                      {displayPopularStoriesTitle}
                    </h2>
                    <div className='featureGridFilter__popular-stories-list'>
                      {popularStories.map((story, index) => (
                        <div
                          key={`popular-story-desktop-${index}`}
                          className='featureGridFilter__popular-story'>
                          <a
                            href={story.link.href}
                            className='featureGridFilter__popular-story-link'
                            target={story.link.target || '_self'}>
                            <h3 className='featureGridFilter__popular-story-headline'>
                              {story.headline}
                            </h3>
                            {renderPopularStoryTags(story.tags)}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Additional Cards - Desktop Only (cards beyond the first 5) */}
          {/* Also show Popular Stories if it's positioned beyond the first set */}
          {(cardsToDisplay.length > 5 ||
            (popularStoriesSetInfo && popularStoriesSetInfo.setIndex > 0)) && (
            <>
              {/* Render additional sets of 5 cards in the same grid format */}
              {/* Calculate sets needed: cards beyond first 5, plus sets needed for Popular Stories if it's beyond first set */}
              {Array.from(
                {
                  length: Math.max(
                    Math.ceil((cardsToDisplay.length - 5) / 5),
                    popularStoriesSetInfo && popularStoriesSetInfo.setIndex > 0
                      ? popularStoriesSetInfo.setIndex + 1
                      : 0
                  ),
                },
                (_, arrayIndex) => {
                  const setIndex = arrayIndex + 1 // Actual set index (1, 2, 3...)
                  const startIndex = 5 + arrayIndex * 5
                  const endIndex = Math.min(
                    startIndex + 5,
                    cardsToDisplay.length
                  )
                  const setCards = cardsToDisplay.slice(startIndex, endIndex)
                  const setListingCount = setCards.length

                  // Helper to check if Popular Stories should be at this position in this set
                  // Popular Stories is positioned AFTER all cards, so we check based on set and position within set
                  // positionInSet should be 1, 3, or 5 (right column positions)
                  // Popular Stories will NOT appear in additional sets if number of results is less than 6
                  const shouldShowPopularStories = (positionInSet: number) => {
                    // Don't show Popular Stories in additional sets if we have fewer than 6 cards
                    if (cardsToDisplay.length < 6) {
                      return false
                    }
                    return (
                      popularStoriesSetInfo?.setIndex === setIndex &&
                      popularStoriesSetInfo?.positionInSet === positionInSet
                    )
                  }

                  return (
                    <div
                      key={`card-set-${setIndex}`}
                      className='featureGridFilter__content featureGridFilter__additional-set'>
                      <div>
                        {setCards[1] && (
                          <div
                            className={classNames(
                              'featureGridFilter__newsCard--2 featureGridFilter__newsCard',
                              { '!mb-0': setListingCount === 2 }
                            )}>
                            <NewsCard
                              {...setCards[1]}
                              image={{
                                ...setCards[1].image!,
                                usePixelMask: false,
                                aspect: {
                                  mobile: '1x1',
                                  tablet: '1x1',
                                  desktop: '1x1',
                                },
                              }}
                            />
                          </div>
                        )}
                        {setListingCount >= 4 && setCards[3] && (
                          <div
                            className={classNames(
                              'featureGridFilter__newsCard--4 featureGridFilter__newsCard',
                              {
                                '!mb-0':
                                  setListingCount === 4 ||
                                  setListingCount === 5,
                              }
                            )}>
                            <NewsCard
                              {...setCards[3]}
                              image={{
                                ...setCards[3].image!,
                                usePixelMask: false,
                                aspect: {
                                  mobile: '1x1',
                                  tablet: '4x3',
                                  desktop: '4x3',
                                },
                              }}
                            />
                          </div>
                        )}
                        {setCards.length >= 5 && setCards[4] && (
                          <div
                            className={classNames(
                              'featureGridFilter__newsCard--5 featureGridFilter__newsCard',
                              { '!mb-0': setListingCount === 5 }
                            )}>
                            <NewsCard
                              {...setCards[4]}
                              image={{
                                ...setCards[4].image!,
                                usePixelMask: false,
                                aspect: {
                                  mobile: '1x1',
                                  tablet: '1x1',
                                  desktop: '1x1',
                                },
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className='featureGridFilter__rightLayout'>
                        {/* Position 1 in this set - Popular Stories or Card */}
                        {shouldShowPopularStories(1) ? (
                          <div className='featureGridFilter__newsCard--1 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop'>
                            <div
                              className={classNames(
                                'featureGridFilter__popular-stories',
                                `featureGridFilter__popular-stories--mask-${pixelLayout}`
                              )}>
                              <h2 className='featureGridFilter__popular-stories-title'>
                                {displayPopularStoriesTitle}
                              </h2>
                              <div className='featureGridFilter__popular-stories-list'>
                                {popularStories.map((story, index) => (
                                  <div
                                    key={`popular-story-set-${setIndex}-${index}`}
                                    className='featureGridFilter__popular-story'>
                                    <a
                                      href={story.link.href}
                                      className='featureGridFilter__popular-story-link'
                                      target={story.link.target || '_self'}>
                                      <h3 className='featureGridFilter__popular-story-headline'>
                                        {story.headline}
                                      </h3>
                                      {renderPopularStoryTags(story.tags)}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          setCards[0] && (
                            <div className='featureGridFilter__newsCard--1 featureGridFilter__newsCard'>
                              <NewsCard
                                {...setCards[0]}
                                image={{
                                  ...setCards[0].image!,
                                  usePixelMask: effectiveUsePixelMask,
                                  aspect: {
                                    mobile: '1x1',
                                    tablet: '4x3',
                                    desktop: '4x3',
                                  },
                                }}
                              />
                            </div>
                          )
                        )}
                        {/* Position 3 in this set - Popular Stories or Card */}
                        {shouldShowPopularStories(3) ? (
                          <div
                            className={classNames(
                              'featureGridFilter__newsCard--3 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop',
                              { '!mb-0': setListingCount === 3 }
                            )}>
                            <div
                              className={classNames(
                                'featureGridFilter__popular-stories',
                                `featureGridFilter__popular-stories--mask-${pixelLayout}`
                              )}>
                              <h2 className='featureGridFilter__popular-stories-title'>
                                {displayPopularStoriesTitle}
                              </h2>
                              <div className='featureGridFilter__popular-stories-list'>
                                {popularStories.map((story, index) => (
                                  <div
                                    key={`popular-story-set-${setIndex}-${index}`}
                                    className='featureGridFilter__popular-story'>
                                    <a
                                      href={story.link.href}
                                      className='featureGridFilter__popular-story-link'
                                      target={story.link.target || '_self'}>
                                      <h3 className='featureGridFilter__popular-story-headline'>
                                        {story.headline}
                                      </h3>
                                      {renderPopularStoryTags(story.tags)}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          setListingCount >= 3 &&
                          setCards[2] && (
                            <div
                              className={classNames(
                                'featureGridFilter__newsCard--3 featureGridFilter__newsCard',
                                { '!mb-0': setListingCount === 3 }
                              )}>
                              <NewsCard
                                {...setCards[2]}
                                image={{
                                  ...setCards[2].image!,
                                  usePixelMask: effectiveUsePixelMask,
                                  aspect: {
                                    mobile: '1x1',
                                    tablet: '1x1',
                                    desktop: '1x1',
                                  },
                                }}
                              />
                            </div>
                          )
                        )}
                        {/* Position 5 in this set - Popular Stories or Card */}
                        {shouldShowPopularStories(5) ? (
                          <div
                            className={classNames(
                              'featureGridFilter__newsCard--5 featureGridFilter__newsCard featureGridFilter__popular-stories--desktop',
                              { '!mb-0': setListingCount === 5 }
                            )}>
                            <div
                              className={classNames(
                                'featureGridFilter__popular-stories',
                                `featureGridFilter__popular-stories--mask-${pixelLayout}`
                              )}>
                              <h2 className='featureGridFilter__popular-stories-title'>
                                {displayPopularStoriesTitle}
                              </h2>
                              <div className='featureGridFilter__popular-stories-list'>
                                {popularStories.map((story, index) => (
                                  <div
                                    key={`popular-story-set-${setIndex}-${index}`}
                                    className='featureGridFilter__popular-story'>
                                    <a
                                      href={story.link.href}
                                      className='featureGridFilter__popular-story-link'
                                      target={story.link.target || '_self'}>
                                      <h3 className='featureGridFilter__popular-story-headline'>
                                        {story.headline}
                                      </h3>
                                      {renderPopularStoryTags(story.tags)}
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                }
              )}
            </>
          )}

          <div
            className='featureGridFilter__newsCard--mobile'
            data-type='feature-grid-mobile'>
            {cardsToDisplay?.map((card, index) => {
              // Determine aspect ratio based on position in grid (every 5 cards is a set)
              const positionInSet = index % 5
              // Position 0 (right top) and position 3 (left middle) get 4x3
              const isLargeCard = positionInSet === 0 || positionInSet === 3

              return (
                <NewsCard
                  {...card}
                  image={{
                    ...card.image!,
                    usePixelMask: effectiveUsePixelMask,
                    aspect: isLargeCard
                      ? {
                          mobile: '1x1',
                          tablet: '4x3',
                          desktop: '4x3',
                        }
                      : {
                          mobile: '1x1',
                          tablet: '1x1',
                          desktop: '1x1',
                        },
                  }}
                  key={`feature-grid-card-${index}`}
                />
              )
            })}
          </div>

          {/* Popular Stories Section - Mobile Only */}
          {popularStories &&
            popularStories.length > 0 &&
            cardsToDisplay.length > 0 && (
              <div
                className={classNames(
                  'featureGridFilter__popular-stories',
                  'featureGridFilter__popular-stories--mobile',
                  `featureGridFilter__popular-stories--mask-${pixelLayout}`
                )}>
                <h2 className='featureGridFilter__popular-stories-title'>
                  {displayPopularStoriesTitle}
                </h2>
                <div className='featureGridFilter__popular-stories-list'>
                  {popularStories.map((story, index) => (
                    <div
                      key={`popular-story-mobile-${index}`}
                      className='featureGridFilter__popular-story'>
                      <a
                        href={story.link.href}
                        className='featureGridFilter__popular-story-link'
                        target={story.link.target || '_self'}>
                        <h3 className='featureGridFilter__popular-story-headline'>
                          {story.headline}
                        </h3>
                        {renderPopularStoryTags(story.tags)}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* No Results Message - Show when no results found (similar to SearchResults) */}
          {!error &&
            currentSearch &&
            ((isApiMode &&
              apiResults &&
              Array.isArray(apiResults) &&
              apiResults.length === 0 &&
              (currentSearch.Term ||
                (currentSearch.Filters && currentSearch.Filters.length > 0))) ||
              (!isApiMode &&
                filteredCards.length === 0 &&
                (searchTerm || selectedFilter !== 'all'))) && (
              <div className='featureGridFilter__no-results'>
                <ResultsCount
                  totalItems={pagination?.totalItems || 0}
                  pageSize={pagination?.pageSize || defaults.pageSize}
                  currentPage={pagination?.currentPage || 1}
                />
              </div>
            )}

          {/* Load More Stories Button */}
          {hasMoreCards && (
            <div className='featureGridFilter__load-more'>
              {loadMore && <Loading text='Loading more stories...' />}
              {!loadMore && (
                <Button
                  type='primary'
                  color='primary'
                  size='base'
                  customAttributes={{
                    onClick: handleLoadMore,
                    type: 'button',
                  }}>
                  Load more
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </BlockWrapper>
  )
}

export default FeatureGridFilter
